import { PageLoader } from '@/components/PageLoader';
import { Select, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { useBranchCalendar, useItemUnits, useItems, type UnitCalendar } from '@/api/masters';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { CameraScanner } from '@/components/CameraScanner';
import { HeldByLine } from '@/components/HeldBy';
import {
  Button,
  Card,
  EmptyState,
  Notice,
  PageHeading,
  SectionTitle,
} from '@/components/PageHeading';
import { hueClasses, type Hue } from '@/components/hues';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { useMediaQuery } from '@/components/useMediaQuery';
import { formatDate, toIsoDate } from '@core/format/dates';
import type { Session } from '@core/session/types';

import {
  agenda,
  barsFor,
  bookingsInWindow,
  eachDay,
  monthGrid,
  monthWindow,
  scheduleOn,
  shiftMonth,
  startOfMonth,
  toneLabel,
  unitMarks,
  unitRuns,
  type Bar,
  type BlockTone,
  type DayMark,
  type ScheduledBooking,
} from '@core/calendar/calendar';

/**
 * Below this, lists. The same breakpoint `DataView` uses, and for the same reason.
 *
 * A seven-column month in 325px of card shows two and a half columns, and a rail row spends 224px
 * of that on the garment's name before a single day is drawn — measured, not guessed. That is the
 * horizontally-scrolling strip the list components exist to avoid, so the calendar does what they
 * do: it changes presentation rather than shrinking.
 */
const listsBelow = '(max-width: 639.98px)';

/**
 * The month view — "what is free in March", which the availability search deliberately refuses.
 *
 * <p>
 * Two tabs over one fetch. <strong>Schedule</strong> is days, and what a counter has to do on each
 * of them: garments leaving, garments due back. <strong>Rail</strong> is the same month turned
 * ninety degrees — a row per garment, a bar per block — which is the shape that answers "is this
 * sherwani free on the 12th, and if not, why not".
 * </p>
 *
 * <p>
 * They read one response on purpose. Two endpoints would let the schedule show a booking the rail
 * does not, and a shop would have no way to tell which of its own screens was lying.
 * </p>
 *
 * <p>
 * <strong>There is no calendar library here, and that was a decision.</strong> The template's own
 * calendar page is FullCalendar; its free build draws events on a month grid but cannot draw rows
 * of garments against days at all — that is its paid Scheduler. Since the rail had to be built by
 * hand either way, both views are, from one grid, so they cannot drift apart in look or behaviour.
 * The arithmetic lives in <c>calendar/calendar.ts</c> and is tested; what is left here is markup.
 * </p>
 */
export function CalendarPage({ session }: { session: Session }) {
  const today = toIsoDate(new Date()) ?? new Date().toISOString().slice(0, 10);

  const [anchor, setAnchor] = useState(() => startOfMonth(today));
  const [tab, setTab] = useState<'schedule' | 'rail'>('schedule');
  const [itemId, setItemId] = useState<number | undefined>();
  const [onlyBusy, setOnlyBusy] = useState(false);
  const [garment, setGarment] = useState<PickerOption | null>(null);
  const [serialSearch, setSerialSearch] = useState('');
  const [scanning, setScanning] = useState(false);

  /**
   * A serial read off a camera, waiting for the lookup behind it.
   *
   * The camera returns text, and this screen needs a garment — so the read sets the search the
   * picker already runs and this remembers what to match when the rows land. Held until it either
   * matches or is answered "no such tag", so a misread says so rather than silently doing nothing.
   */
  const [pendingScan, setPendingScan] = useState<string | null>(null);
  const [scanMiss, setScanMiss] = useState<string | null>(null);

  const narrow = useMediaQuery(listsBelow);

  // The tag search. `pageSize` is small on purpose: a scanner types a whole serial and this is a
  // confirmation, not a browse — and the picker says "keep typing" when the total outruns the list.
  const scanned = useItemUnits(session.branch?.id, { search: serialSearch, pageSize: 8 });

  const unitId = garment === null ? undefined : Number(garment.id);

  /**
   * Turns a scanned serial into the chosen garment, once its row arrives.
   *
   * <strong>Exact match only.</strong> The search is a `LIKE` and a tag reading `JP001` also
   * matches `JP0012`; picking the first row would put a neighbouring garment on the screen and
   * nothing would look wrong. A tag either names one garment or it names none.
   */
  useEffect(() => {
    if (pendingScan === null || scanned.isLoading) {
      return;
    }

    const exact = (scanned.data?.items ?? []).find(
      (row) => row.serialCode.toUpperCase() === pendingScan.toUpperCase(),
    );

    if (exact) {
      setGarment({
        id: String(exact.id),
        primary: exact.serialCode,
        secondary: `${exact.itemName} · ${exact.variantDisplayName}`,
      });
      setScanMiss(null);
    } else {
      setScanMiss(pendingScan);
    }

    setPendingScan(null);
  }, [pendingScan, scanned.isLoading, scanned.data]);

  const items = useItems(session.branch?.id, { includeInactive: true, pageSize: 200 });

  const weeks = useMemo(() => monthGrid(anchor, today), [anchor, today]);
  const window = useMemo(() => monthWindow(anchor, today), [anchor, today]);

  const calendar = useBranchCalendar(session.branch?.id, window.from, window.to, {
    itemId,
    unitId,
    onlyBusy,
  });

  // Its own memo, not an inline `?? []`: a fresh array literal every render makes the memo below
  // recompute the whole month's bookings on every keystroke elsewhere on the page.
  const units = useMemo(() => calendar.data?.units ?? [], [calendar.data?.units]);

  const bookings = useMemo(
    () => bookingsInWindow(units, window.from, window.to),
    [units, window.from, window.to],
  );

  /**
   * The focused garment's own month, when a tag has been scanned.
   *
   * The response already holds exactly one row in that case, so the marks come off the same fetch
   * as everything else — the schedule and the rail cannot disagree about which days it is out.
   */
  const focused = unitId === undefined ? null : (units[0] ?? null);

  const marks = useMemo(
    () => (focused ? unitMarks(focused.blocks, window.from, window.to) : null),
    [focused, window.from, window.to],
  );

  /**
   * The same month as a list of runs, which is where the party behind each one fits.
   *
   * A day cell holds a coloured band and a truncated name and nothing else, so the answer to the
   * question somebody holding the garment is actually asking — <em>whose is this, what is the
   * booking number, what is their number</em> — has to be printed under the month rather than
   * inside it.
   */
  const runs = useMemo(
    () => (focused ? unitRuns(focused.blocks, window.from, window.to) : null),
    [focused, window.from, window.to],
  );

  const monthLabel = new Date(`${anchor}T00:00:00Z`).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <>
      <PageHeading
        title="Calendar"
        subtitle="The month at a glance — what leaves, what comes back, and what is still on the rail."
        actions={
          <>
            <Button
              icon="icon icon-chevron-left"
              aria-label="Previous month"
              onClick={() => setAnchor(shiftMonth(anchor, -1))}
            />
            <Button onClick={() => setAnchor(startOfMonth(today))}>This month</Button>
            <Button
              icon="icon icon-chevron-right"
              aria-label="Next month"
              onClick={() => setAnchor(shiftMonth(anchor, 1))}
            />
          </>
        }
      />

      {calendar.isError && <ApiErrorAlert error={calendar.error} />}

      {scanning && (
        <CameraScanner
          onClose={() => setScanning(false)}
          onRead={(serial) => {
            setScanning(false);
            setScanMiss(null);
            // The picker's own query is what resolves it — one lookup, not a second endpoint that
            // could disagree with the one the typed field uses.
            setSerialSearch(serial);
            setPendingScan(serial);
          }}
        />
      )}

      {scanMiss !== null && (
        <div className="mb-3">
          <Notice tone="warning" title={`No garment with the tag ${scanMiss}`}>
            Nothing on this branch&rsquo;s rail carries that serial. Check the tag is this
            shop&rsquo;s, or type part of the code to search for it.
          </Notice>
        </div>
      )}

      <Card>
        <SectionTitle
          icon="icon icon-calendar-range"
          hue="primary"
          trailing={
            <div
              className="flex rounded-md border border-border-color p-0.5"
              role="tablist"
              aria-label="Calendar view"
            >
              {(['schedule', 'rail'] as const).map((name) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={tab === name}
                  onClick={() => setTab(name)}
                  className={`rounded px-3 py-1 text-[length:var(--scale-font-size)] capitalize ${
                    tab === name ? 'bg-primary text-white' : 'text-default'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          }
        >
          {monthLabel}
        </SectionTitle>

        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {/*
              The tag search. A handheld scanner is a keyboard: it types the serial and presses
              Enter, which is what the picker's own keyboard handling already does with the first
              match — so scanning needs no decoder here. The QR the alteration docket prints carries
              the plain serial code, so what lands in this box is exactly what it matches on.
            */}
            <div className="w-64">
              <SearchPicker
                value={garment}
                options={(scanned.data?.items ?? []).map((row) => ({
                  id: String(row.id),
                  primary: row.serialCode,
                  secondary: `${row.itemName} · ${row.variantDisplayName}`,
                }))}
                total={scanned.data?.total ?? 0}
                loading={scanned.isLoading}
                placeholder="Scan a tag or type a serial"
                onSearch={setSerialSearch}
                onSelect={(option) => {
                  setGarment(option);
                  setScanMiss(null);
                }}
              />
            </div>

            {/*
              The camera, for the phone at the rail where there is no keyboard and no handheld
              scanner. Never the only way in — the field beside it is.
            */}
            <Button icon="icon icon-scan-line" onClick={() => setScanning(true)}>
              Camera
            </Button>

            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Every design"
              className="min-w-56"
              value={itemId}
              onChange={(value?: number) => setItemId(value)}
              options={(items.data?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />

            <label className="flex items-center gap-2 text-[length:var(--scale-font-size)] text-default">
              <input
                type="checkbox"
                checked={onlyBusy}
                onChange={(event) => setOnlyBusy(event.target.checked)}
              />
              Only garments with something on them
            </label>
          </div>
        </div>

        {calendar.isLoading && <PageLoader label="Loading the calendar" />}

        {focused && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-[length:var(--scale-font-size)] font-semibold text-gray-900">
              {focused.serialCode}
            </span>
            <span className="text-[length:var(--scale-list-meta)] text-default">
              {focused.itemName} · {focused.variantDisplayName}
            </span>
            {marks?.size === 0 && (
              <span className="text-[length:var(--scale-list-meta)] text-success">
                Free all month
              </span>
            )}
          </div>
        )}

        {!calendar.isLoading &&
          tab === 'schedule' &&
          (narrow ? (
            <ScheduleAgenda from={window.from} to={window.to} bookings={bookings} runs={runs} />
          ) : (
            <>
              <ScheduleGrid weeks={weeks} bookings={bookings} marks={marks} />

              {/*
                Nothing when the garment is free: the line above the grid already says "Free all
                month", and an empty section under it saying the same thing a third time reads as a
                screen that has lost its data. The narrow view keeps the empty state, because there
                the list is the whole answer rather than a footnote to a grid.
              */}
              {runs && runs.length > 0 && (
                <div className="mt-5">
                  <SectionTitle icon="icon icon-user-round" hue="primary">
                    Who has it this month
                  </SectionTitle>
                  <UnitRunList runs={runs} />
                </div>
              )}
            </>
          ))}

        {!calendar.isLoading &&
          tab === 'rail' &&
          (narrow ? (
            <RailList units={units} from={window.from} to={window.to} />
          ) : (
            <RailGrid units={units} from={window.from} to={window.to} today={today} />
          ))}

        {calendar.data?.hasMore && (
          <div className="mt-4">
            <Notice tone="warning" title="Only part of the rail is shown">
              This branch holds more garments than one calendar can lay out. Narrow to a design
              above — a month of five hundred rows is not a calendar.
            </Notice>
          </div>
        )}
      </Card>

      <div className="mt-4">
        <Legend />
      </div>
    </>
  );
}

/** "Tue 1 Sep" — weekday first, because an agenda is read down a column of days. */
function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/* --------------------------------------------------------------- the schedule */

function ScheduleGrid({
  weeks,
  bookings,
  marks,
}: {
  weeks: ReturnType<typeof monthGrid>;
  bookings: ScheduledBooking[];
  /**
   * One garment's occupancy, when a tag has been scanned. The cells then mark the days it is
   * spoken for instead of listing the shop's comings and goings — the same grid answering the
   * other question a counter asks: not "what happens today" but "when is this piece free".
   */
  marks: Map<string, DayMark> | null;
}) {
  const headings = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    // Wide content scrolls inside its own container, never the page body.
    <div className="overflow-x-auto">
      <div className="min-w-[52rem]">
        <div className="grid grid-cols-7 gap-px">
          {headings.map((heading) => (
            <div
              key={heading}
              className="px-2 py-1 text-[length:var(--scale-list-meta)] font-medium text-default"
            >
              {heading}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px rounded-md bg-border-color">
          {weeks.flat().map((cell) => {
            const day = scheduleOn(cell.iso, bookings);

            return (
              <div
                key={cell.iso}
                // One background class, chosen — not `bg-white` with `bg-light` appended after it.
                // Two conflicting utilities on one element are resolved by stylesheet order rather
                // than by the order they appear in the attribute, so `bg-white` won and the weekend
                // tint never rendered in either theme.
                className={`min-h-24 p-1.5 ${cell.isWeekend ? 'bg-light' : 'bg-white'} ${
                  cell.inMonth ? '' : 'opacity-45'
                }`}
              >
                <div
                  className={`mb-1 text-[length:var(--scale-list-meta)] ${
                    cell.isToday
                      ? 'inline-flex size-5 items-center justify-center rounded-full bg-primary font-semibold text-white'
                      : 'text-default'
                  }`}
                >
                  {cell.day}
                </div>

                {marks ? (
                  <DayBand mark={marks.get(cell.iso) ?? null} />
                ) : (
                  <ul className="flex list-none flex-col gap-1 ps-0">
                    {day.out.map((booking) => (
                      <li key={`out-${booking.bookingId}`} className="list-none">
                        <BookingChip booking={booking} direction="out" />
                      </li>
                    ))}
                    {day.back.map((booking) => (
                      <li key={`back-${booking.bookingId}`} className="list-none">
                        <BookingChip booking={booking} direction="back" />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * One day of the focused garment's month: a band when it is spoken for, nothing when it is free.
 *
 * <strong>Nothing is the answer, not an omission.</strong> Somebody holding a garment and looking
 * at the month is asking which days are empty, so an empty cell has to read as "yours" — which is
 * why the free days carry no wash, no dash and no label to compete with the runs either side.
 *
 * The label rides only on the day a run starts, so 3rd-to-6th reads as one booking rather than four.
 */
function DayBand({ mark }: { mark: DayMark | null }) {
  if (mark === null) {
    return null;
  }

  const hue = barHue[mark.tone];
  const holder = mark.block.heldBy;

  // **Only a booking is named for its customer.** The turnaround behind one carries the same
  // `heldBy`, so labelling every band by the holder printed "Hiren Patel" on the cleaning day too —
  // and a garment's own month then read as three bookings where there are two. Everything that is
  // not somebody's booking is named for what it is, which is what the legend already calls it.
  const isBooking = mark.tone === 'booked' || mark.tone === 'tentative';

  const named = isBooking ? (holder?.customerName ?? toneLabel[mark.tone]) : toneLabel[mark.tone];

  // The band is one line of six-point type, so the rest of the identification rides in the tooltip
  // — the booking's own number and the payer's mobile, which is what a clerk holding the garment
  // reaches for. It is the hover copy of the run list under the month, not a second answer: both
  // read the same `heldBy`, and only a booking's band names a party for `DayBand`'s reason above.
  const title = [
    named,
    holder && named !== holder.customerName ? holder.customerName : null,
    isBooking && holder ? (holder.documentNumber ?? 'not yet numbered') : null,
    isBooking ? (holder?.customerMobile ?? null) : null,
    `${formatDate(mark.block.fromDate)} → ${formatDate(mark.block.toDate)}`,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <div
      className={`flex h-6 items-center gap-1 overflow-hidden px-1 ${hueClasses[hue].tile} ${
        mark.isStart ? 'rounded-s-sm' : ''
      } ${mark.isEnd ? 'rounded-e-sm' : ''}`}
      title={title}
    >
      {mark.isStart && (
        <span className="truncate text-[length:var(--scale-badge)] font-medium text-white">
          {named}
        </span>
      )}
    </div>
  );
}

/**
 * The focused garment's runs, with the party behind each one.
 *
 * <p>
 * <strong>This is the half a day cell cannot hold.</strong> A band in the grid is a strip of
 * colour and a truncated name; somebody standing at the rail with the garment in their hand is
 * asking the three things that do not fit in it — <em>which booking, whose, and what is their
 * number</em>. So the same runs are listed under the month, and on a phone they are the whole
 * answer.
 * </p>
 *
 * <p>
 * The identification goes through <c>HeldByLine</c>, which the availability search and the booking
 * counter already use for exactly this moment. Three screens naming a booking three ways is how a
 * shop stops trusting any of them — and it is the component that knows a hold takes no number and
 * says so rather than leaving a gap that reads as missing data.
 * </p>
 *
 * <p>
 * <strong>Only a booking is named for its party.</strong> The turnaround behind one carries the
 * same <c>heldBy</c>, so printing the payer against it reads as a second booking — the trap
 * <c>DayBand</c> already avoids, restated here because this list is where it would be most
 * convincing.
 * </p>
 */
function UnitRunList({ runs }: { runs: Bar[] }) {
  if (runs.length === 0) {
    return (
      <EmptyState
        icon="icon icon-calendar-check"
        title="Free all month"
        hint="Nothing has this garment over this window."
      />
    );
  }

  return (
    // `ps-0` on the list and `list-none` on each row: the template indents the list and puts the
    // marker on a descendant selector, so undoing it takes both, in those two places.
    //
    // Named, because on a phone this list replaces the whole month and there is no heading above it
    // to say what it is — the grid's own section title exists only on a desk.
    <ol aria-label="Who has this garment" className="flex list-none flex-col gap-2.5 ps-0">
      {runs.map((run) => {
        const holder = run.block.heldBy;
        const isBooking = run.tone === 'booked' || run.tone === 'tentative';

        return (
          <li key={run.block.id} className="flex list-none items-start gap-2">
            <span
              className={`mt-1 size-2.5 shrink-0 rounded-sm ${hueClasses[barHue[run.tone]].tile}`}
              aria-hidden="true"
            />

            <span className="min-w-0 flex-1">
              <span className="block text-[length:var(--scale-list-meta)] text-default">
                <span className="text-gray-900">
                  {formatDate(run.block.fromDate)} → {formatDate(run.block.toDate)}
                </span>{' '}
                · {toneLabel[run.tone]}
              </span>

              {isBooking && holder && (
                <HeldByLine holder={holder} tentative={run.tone === 'tentative'} />
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function BookingChip({
  booking,
  direction,
}: {
  booking: ScheduledBooking;
  direction: 'out' | 'back';
}) {
  // Out and back are the same booking seen from two mornings, so they take one hue and are told
  // apart by the arrow — two hues would read as two kinds of booking.
  const hue: Hue = booking.isTentative ? 'warning' : direction === 'out' ? 'primary' : 'teal';

  return (
    <Tooltip
      title={`${booking.documentNumber ?? 'On hold'} · ${booking.customerName} · ${
        booking.garments
      } garment${booking.garments === 1 ? '' : 's'}`}
    >
      <span
        className={`flex items-center gap-1 truncate rounded px-1.5 py-0.5 text-[length:var(--scale-badge)] ${
          hueClasses[hue].wash
        } ${hueClasses[hue].ink}`}
      >
        <i
          className={`icon ${direction === 'out' ? 'icon-arrow-up-right' : 'icon-arrow-down-left'}`}
          aria-hidden="true"
        />
        <span className="truncate">{booking.customerName}</span>
        {booking.garments > 1 && <span className="ms-auto">{booking.garments}</span>}
      </span>
    </Tooltip>
  );
}

/**
 * The month as a list of the days something happens on — the schedule on a phone.
 *
 * Not "here is October" but "here is what you do, in order", which is the only shape that survives
 * a 380px screen. The grid's own window, so both presentations agree about what is in view.
 */
function ScheduleAgenda({
  from,
  to,
  bookings,
  runs,
}: {
  from: string;
  to: string;
  bookings: ScheduledBooking[];
  /** The focused garment's runs, when a tag has been scanned. Dates, not comings and goings. */
  runs: Bar[] | null;
}) {
  const days = useMemo(() => agenda(from, to, bookings), [from, to, bookings]);

  // A garment's month on a phone is its runs, in order — "3–6 Sept, then 8–10 Sept" is the whole
  // answer, and splitting it back into out-days and back-days would make a clerk reassemble it.
  if (runs) {
    return <UnitRunList runs={runs} />;
  }

  if (days.length === 0) {
    return (
      <EmptyState
        icon="icon icon-calendar-check"
        title="Nothing leaves or comes back this month"
        hint="No garment is due out or due in over this window."
      />
    );
  }

  return (
    // `ps-0` on the list and `list-none` on each row: the template indents the list and puts the
    // marker on a descendant selector, so undoing it takes both, in those two places.
    <ol className="flex list-none flex-col gap-4 ps-0">
      {days.map((day) => (
        <li key={day.iso} className="list-none border-s-2 border-border-color ps-3">
          <div className="mb-1.5 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {dayLabel(day.iso)}
          </div>

          <div className="flex flex-col gap-1">
            {day.out.map((booking) => (
              <BookingLine key={`out-${booking.bookingId}`} booking={booking} direction="out" />
            ))}
            {day.back.map((booking) => (
              <BookingLine key={`back-${booking.bookingId}`} booking={booking} direction="back" />
            ))}
          </div>
        </li>
      ))}
    </ol>
  );
}

function BookingLine({
  booking,
  direction,
}: {
  booking: ScheduledBooking;
  direction: 'out' | 'back';
}) {
  const hue: Hue = booking.isTentative ? 'warning' : direction === 'out' ? 'primary' : 'teal';

  return (
    <div className="flex items-center gap-2">
      <span
        className={`flex size-6 shrink-0 items-center justify-center rounded ${hueClasses[hue].wash} ${hueClasses[hue].ink}`}
      >
        <i
          className={`icon ${direction === 'out' ? 'icon-arrow-up-right' : 'icon-arrow-down-left'}`}
          aria-hidden="true"
        />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[length:var(--scale-font-size)] font-semibold text-gray-900">
          {booking.customerName}
        </span>
        <span className="block truncate text-[length:var(--scale-list-meta)] text-default">
          {direction === 'out' ? 'Going out' : 'Coming back'} ·{' '}
          {booking.documentNumber ?? 'On hold'} · {booking.garments} garment
          {booking.garments === 1 ? '' : 's'}
        </span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------- the rail */

const barHue: Record<BlockTone, Hue> = {
  booked: 'primary',
  // Amber is needs-attention everywhere, and a claim that can still lapse is exactly that.
  tentative: 'warning',
  cleaning: 'cyan',
  tailor: 'purple',
  other: 'secondary',
};

function RailGrid({
  units,
  from,
  to,
  today,
}: {
  units: UnitCalendar[];
  from: string;
  to: string;
  today: string;
}) {
  const days = useMemo(() => eachDay(from, to), [from, to]);

  if (units.length === 0) {
    return (
      <EmptyState
        icon="icon icon-shirt"
        title="No garments on this rail"
        hint="Nothing matches the design chosen above, or this branch has no stock yet."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: `${14 + days.length * 1.75}rem` }}>
        <div
          className="grid items-center gap-px"
          style={{ gridTemplateColumns: `14rem repeat(${days.length}, minmax(1.75rem, 1fr))` }}
        >
          <div />
          {days.map((iso) => (
            <div
              key={iso}
              className={`text-center text-[length:var(--scale-list-meta)] ${
                iso === today ? 'font-semibold text-primary' : 'text-default'
              }`}
            >
              {Number(iso.slice(8, 10))}
            </div>
          ))}
        </div>

        {units.map((unit) => (
          <RailRow key={unit.unitId} unit={unit} days={days} from={from} to={to} />
        ))}
      </div>
    </div>
  );
}

function RailRow({
  unit,
  days,
  from,
  to,
}: {
  unit: UnitCalendar;
  days: string[];
  from: string;
  to: string;
}) {
  const bars = useMemo(() => barsFor(unit.blocks, from, to), [unit.blocks, from, to]);

  return (
    <div
      className="grid items-center gap-px border-t border-border-color py-1"
      style={{ gridTemplateColumns: `14rem repeat(${days.length}, minmax(1.75rem, 1fr))` }}
    >
      <div className="pe-2">
        <div className="truncate text-[length:var(--scale-font-size)] font-semibold text-gray-900">
          {unit.serialCode}
        </div>
        <div className="truncate text-[length:var(--scale-list-meta)] text-default">
          {unit.variantDisplayName}
        </div>
      </div>

      {/* The day cells, drawn first so the bars sit over a continuous strip. */}
      {days.map((iso) => (
        <div key={iso} className="h-6 rounded-sm bg-light" />
      ))}

      {bars.map((bar) => (
        <BarChip key={bar.block.id} bar={bar} />
      ))}
    </div>
  );
}

function BarChip({ bar }: { bar: Bar }) {
  const hue = barHue[bar.tone];

  return (
    <Tooltip title={<BarTitle bar={bar} />}>
      <div
        className={`h-6 ${hueClasses[hue].tile} ${bar.clippedStart ? '' : 'rounded-s-sm'} ${
          bar.clippedEnd ? '' : 'rounded-e-sm'
        }`}
        style={{
          // +2 because the grid's first column is the garment's name, and CSS grid lines are
          // 1-based — an off-by-one here draws every booking a day early, which looks entirely
          // plausible and is why the offsets are computed in a tested module rather than here.
          gridColumn: `${bar.offset + 2} / span ${bar.length}`,
          gridRow: 1,
        }}
      />
    </Tooltip>
  );
}

/**
 * The rail on a phone: a garment, then what is on it, in words.
 *
 * The bars become lines because a 30-day strip cannot fit — but they are the same bars, off the
 * same `barsFor`, so the clipping and the released-block rule hold here without being restated.
 * **"Free all month" is a row, not an omission**, for the reason the whole view is read rail-first.
 */
function RailList({ units, from, to }: { units: UnitCalendar[]; from: string; to: string }) {
  if (units.length === 0) {
    return (
      <EmptyState
        icon="icon icon-shirt"
        title="No garments on this rail"
        hint="Nothing matches the design chosen above, or this branch has no stock yet."
      />
    );
  }

  return (
    <ol className="flex list-none flex-col ps-0">
      {units.map((unit) => (
        <RailListRow key={unit.unitId} unit={unit} from={from} to={to} />
      ))}
    </ol>
  );
}

function RailListRow({ unit, from, to }: { unit: UnitCalendar; from: string; to: string }) {
  const bars = useMemo(() => barsFor(unit.blocks, from, to), [unit.blocks, from, to]);

  return (
    <li className="list-none border-t border-border-color py-2.5 first:border-t-0 first:pt-0">
      <div className="truncate text-[length:var(--scale-font-size)] font-semibold text-gray-900">
        {unit.serialCode}
      </div>
      <div className="mb-1.5 truncate text-[length:var(--scale-list-meta)] text-default">
        {unit.variantDisplayName}
      </div>

      {bars.length === 0 ? (
        <div className="text-[length:var(--scale-list-meta)] text-success">Free all month</div>
      ) : (
        <div className="flex flex-col gap-1">
          {bars.map((bar) => (
            <div key={bar.block.id} className="flex items-start gap-2">
              <span
                className={`mt-1 size-2.5 shrink-0 rounded-sm ${hueClasses[barHue[bar.tone]].tile}`}
                aria-hidden="true"
              />
              <span className="text-[length:var(--scale-list-meta)] text-default">
                <span className="text-gray-900">
                  {bar.block.heldBy?.customerName ?? toneLabel[bar.tone]}
                </span>{' '}
                · {formatDate(bar.block.fromDate)} → {formatDate(bar.block.toDate)}
                {bar.block.heldBy && ` · ${toneLabel[bar.tone]}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

function BarTitle({ bar }: { bar: Bar }) {
  const { block } = bar;

  return (
    <span>
      {block.heldBy
        ? `${block.heldBy.documentNumber ?? 'On hold'} · ${block.heldBy.customerName}`
        : block.blockType}
      <br />
      {formatDate(block.fromDate)} → {formatDate(block.toDate)}
      {block.isBuffer && ' · turnaround'}
    </span>
  );
}

/**
 * The key to the colours — and to the words, since the narrow rail names its rows from the same
 * `toneLabel` map. Two lists of names for one set of bars is how a legend starts calling something
 * one thing while the row beside it calls it another.
 */
function Legend() {
  const tones: BlockTone[] = ['booked', 'tentative', 'cleaning', 'tailor', 'other'];

  return (
    <div className="flex flex-wrap items-center gap-4">
      {tones.map((tone) => (
        <span
          key={tone}
          className="flex items-center gap-2 text-[length:var(--scale-list-meta)] text-default"
        >
          <span className={`size-3 rounded-sm ${hueClasses[barHue[tone]].tile}`} />
          {toneLabel[tone]}
        </span>
      ))}
    </div>
  );
}
